import { Link } from "react-router-dom";
import { Lock, Linkedin } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { CATEGORY_PAGES, categoryLabel } from "@/lib/categories";
import LBLogo from "@/components/LBLogo";
import NewsletterForm from "@/components/NewsletterForm";

export default function Footer() {
  const { lang, tr } = useLang();
  const nav = tr("nav");
  const f = tr("footer");

  const links = [
    { to: "/about", label: nav.about },
    { to: "/advisory", label: nav.advisory },
    { to: "/knowledge-center", label: nav.knowledge },
    { to: "/insights", label: nav.insights },
    { to: "/contact", label: nav.contact },
  ];

  return (
    <footer data-testid="site-footer" className="relative overflow-hidden border-t border-gold/15 bg-navy-light noise">
      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-20">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <Link to="/" className="flex items-center gap-4" data-testid="footer-logo-link">
              <LBLogo size={52} />
              <div>
                <p className="font-serif text-2xl text-ivory">LB Trade Advisory</p>
                <p className="mt-1 font-serif text-base italic text-gold-bright">{f.tagline}</p>
              </div>
            </Link>
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-ink-light/50">{f.note}</p>
            <a
              href="https://www.linkedin.com/company/lb-trade-advisory"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-linkedin"
              className="mt-8 inline-flex items-center gap-2.5 rounded-full border border-gold/25 bg-white/[0.03] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-ivory/60 transition-colors hover:border-gold/50 hover:text-gold-bright"
            >
              <Linkedin className="h-3.5 w-3.5 text-gold" />
              {f.follow}
            </a>
          </div>
          <div className="md:col-span-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Menu</p>
            <ul className="mt-5 space-y-3">
              {links.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} data-testid={`footer-link-${l.to.replace("/", "") || "home"}`} className="text-sm text-ivory/60 transition-colors hover:text-gold-bright">
                    {l.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/guida-incoterms" data-testid="footer-link-guide" className="text-sm text-gold-bright/80 transition-colors hover:text-gold-bright">
                  {f.freeGuide}
                </Link>
              </li>
              <li>
                <Link
                  to="/admin"
                  data-testid="footer-admin-link"
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-ivory/35 transition-colors hover:text-gold-bright"
                >
                  <Lock className="h-3 w-3" /> {f.reserved}
                </Link>
              </li>
            </ul>
            <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.25em] text-gold">{f.categories}</p>
            <ul className="mt-5 space-y-3">
              {CATEGORY_PAGES.map((p) => (
                <li key={p.slug}>
                  <Link
                    to={`/categoria/${p.slug}`}
                    data-testid={`footer-category-${p.slug}`}
                    className="text-sm text-ivory/60 transition-colors hover:text-gold-bright"
                  >
                    {categoryLabel(p.key, lang)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-5">
            <NewsletterForm />
          </div>
        </div>
      </div>
      <div className="border-t border-white/5 px-6 py-6 lg:px-10">
        <p className="mx-auto max-w-7xl text-center font-mono text-[11px] tracking-[0.15em] text-ivory/35" data-testid="footer-copyright">
          © {new Date().getFullYear()} LB TRADE ADVISORY — {f.rights}
        </p>
      </div>
    </footer>
  );
}
